import React, { useMemo } from 'react';
import { BadgesEarnedWidget } from './BadgesEarnedWidget';
import { CoursesCompletedWidget } from './CoursesCompletedWidget';
import { LearningStreakWidget } from './LearningStreakWidget';
import trendUpArrow from '../../assets/trend-up-arrow.svg';
import trendDownArrow from '../../assets/trend-down-arrow.svg';
import { MarketPulse } from './MarketPulse';
import { TeamUserSummary } from '../../types/teamUserSummary';

const BADGES_ICON_URL = 'https://storage.googleapis.com/rev-public/icons/Badges%20Earned.svg';
const COURSES_COMPLETED_ICON_URL = 'https://storage.googleapis.com/rev-public/icons/Courses%20Completed.svg';
const LEARNING_STREAK_ICON_URL = 'https://storage.googleapis.com/rev-public/icons/Learning%20Streak.svg';

interface WidgetsProps {
  data?: TeamUserSummary;
}

export const Widgets: React.FC<WidgetsProps> = ({ data }) => {
  // Calculate aggregated badge data
  const badgeData = useMemo(() => {
    if (!data || !data.progress || data.progress.length === 0) {
      return {
        count: 0,
        trend: {
          value: 0,
          isPositive: 'same' as const,
          icon: undefined
        }
      };
    }

    const icon =
      data.badgeSummary.trend.isPositive === 'positive' ? trendUpArrow :
      data.badgeSummary.trend.isPositive === 'negative' ? trendDownArrow :
      undefined;

    return {
      count: data.badgeSummary.count,
      trend: {
        value: data.badgeSummary.trend.value,
        isPositive: data.badgeSummary.trend.isPositive,
        icon
      }
    };
  }, [data]);

  // Calculate aggregated courses data
  const coursesData = useMemo(() => {
    if (!data || !data.progress || data.progress.length === 0) {
      return {
        coursesCompleted: 0,
        coursesTotal: 0
      };
    }

    return data.progress.reduce(
      (acc, cert) => ({
        coursesCompleted: acc.coursesCompleted + (cert.coursesCompleted || 0),
        coursesTotal: acc.coursesTotal + (cert.coursesTotal || 0)
      }),
      { coursesCompleted: 0, coursesTotal: 0 }
    );
  }, [data]);

  // Calculate learning streak (pick the longest)
  const streakData = useMemo(() => {
    if (!data || !data.progress || data.progress.length === 0) {
      return {
        currentStreak: 0,
        longestStreak: 0
      };
    }

    return data.progress.reduce(
      (acc, cert) => ({
        currentStreak: Math.max(acc.currentStreak, cert.learningStreak?.currentStreak || 0),
        longestStreak: Math.max(acc.longestStreak, cert.learningStreak?.longestStreak || 0)
      }),
      { currentStreak: 0, longestStreak: 0 }
    );
  }, [data]);

  return (
    <section
      className="flex w-full flex-col items-stretch justify-center p-2.5 max-md:max-w-full"
    >
      <div className="flex flex-col sm:flex-row lg:block gap-2 items-stretch">
        {/* MarketPulse: Visible on XS/SM (left side), hidden on MD+ */}
        <div className="block md:hidden w-full sm:w-1/2">
          <MarketPulse className="h-full" />
        </div>

        {/* Widgets: Stacked on XS (right side), Row on SM+ */}
        <div className="flex flex-col sm:flex-row w-full sm:w-1/2 md:w-full lg:w-full gap-2 justify-between flex-wrap xl:flex-nowrap">
          <BadgesEarnedWidget
            count={badgeData.count}
            trend={badgeData.trend}
            icon={BADGES_ICON_URL}
          />
          <CoursesCompletedWidget
            coursesCompleted={coursesData.coursesCompleted}
            coursesTotal={coursesData.coursesTotal}
            icon={COURSES_COMPLETED_ICON_URL}
          />
          <LearningStreakWidget
            currentStreak={streakData.currentStreak}
            longestStreak={streakData.longestStreak}
            icon={LEARNING_STREAK_ICON_URL}
          />
        </div>
      </div>
    </section>
  );
};
